import { useStyles, Image } from '@archipro-design/aria';
import * as S from './DirectoryPotmTile.style';
import { useRef, useState, useEffect } from 'react';
import { useIsWebmLegacyDevice } from '@modules/root/hook/use-is-webm-legacy-device';
import { useInViewport } from 'ahooks';
import { Link } from '@remix-run/react';
import { useHydrated } from 'remix-utils';
import FilePlayer from 'react-player/file';
import {
    useAdEventHandler,
    useLogImpression,
    useTracker,
} from '@archipro-website/tracker';
import type { AdvertCreative } from '~/modules/adverts/type';
import { createLinkedComponentParams } from '~/utils/urlHelper';

interface PotmTileProps {
    advertCreative: AdvertCreative;
}

const LEGACY_URLS = [
    '/home-design-event',
    '/building-guide',
    '/box-the-architect-builder',
    '/advertising',
    '/careers',
    '/contact-us',
];

const AdvertPotmTileDesktop = ({ advertCreative }: PotmTileProps) => {
    const id = advertCreative.id;
    const title = advertCreative.title;
    const link = advertCreative.cta.href;
    const professionalID = advertCreative.professional.id;
    const professionalName = advertCreative.cta.label;
    const videoCoverDesktop = advertCreative.video?.cover || '';
    const videoWebm = advertCreative.video?.webm || '';
    const videoMp4 = advertCreative.video?.mp4 || '';
    const autoplay = !!advertCreative.video?.autoplay;

    const tileWidth = 437;
    const tileHeight = 790;
    const [playing, setPlaying] = useState(false);
    const [videoSources, setVideoSources] = useState([videoWebm, videoMp4]);
    const hydrated = useHydrated();
    const { css } = useStyles({ tileWidth, tileHeight, playing });
    const ref = useRef<HTMLAnchorElement>(null);
    const isWebmLegacyDevice = useIsWebmLegacyDevice();
    const [inViewport] = useInViewport(ref, {
        threshold: 0.2,
    });

    useEffect(() => {
        setVideoSources(
            isWebmLegacyDevice ? [videoMp4, videoWebm] : [videoWebm, videoMp4]
        );
    }, [videoMp4, videoWebm, isWebmLegacyDevice]);

    const tracker = useTracker();
    useLogImpression({
        data: {
            professionalID: Number(professionalID),
            itemID: Number(id),
            type: 'VerticalTile_Advert',
        },
        ref,
    });

    const { onClick } = useAdEventHandler(advertCreative, ref);
    const clickType =
        (videoMp4 || videoWebm) && autoplay ? 'Ad_VideoClick' : 'Ad_ImageClick';
    const { onClick: trackClick } = createLinkedComponentParams(
        tracker,
        clickType,
        advertCreative.cta.href,
        {
            libraryItemID: advertCreative.professional.id,
            professionalID: advertCreative.professional.id,
            professionalTitle: advertCreative.professional.title,
        },
        null,
        onClick
    );

    //server render only cover image
    const video =
        hydrated && videoSources.length > 0 ? (
            <div className={css(S.Player)}>
                <FilePlayer
                    url={videoSources}
                    playing={autoplay && inViewport}
                    muted={true}
                    loop={true}
                    controls={false}
                    playsinline={true}
                    width={'100%'}
                    height={'100%'}
                    onPlay={() => setPlaying(true)}
                    config={{
                        attributes: {
                            preload: 'none',
                            poster: videoCoverDesktop,
                        },
                    }}
                />
            </div>
        ) : null;

    return LEGACY_URLS.includes(link) ? (
        <a
            href={link || '#'}
            ref={ref}
            className={css(S.DirectoryGridContainer)}
            onClick={trackClick}
        >
            <div className={css(S.VideoAspect)}>
                {video}
                <Image
                    className={css(S.Cover)}
                    src={videoCoverDesktop}
                    width={tileWidth}
                    height={tileHeight}
                    layout={'intrinsic'}
                    objectFit={'cover'}
                    objectPosition={'center'}
                    alt={title || ''}
                    styles={{ width: '100%' }}
                />
            </div>
            <div className={css(S.PotmDetails)}>
                <div className="flex flex-col gap-[0.125rem]">
                    <span className="overflow-hidden text-ellipsis line-clamp-2 text-sm md:text-base font-medium tracking-[0.03rem] md:tracking-[0.02rem]">
                        {title}
                    </span>
                    <span className="overflow-hidden text-ellipsis text-nowrap text-sm md:text-base font-medium tracking-[0.03rem] md:tracking-[0.02rem] text-gray-900/40">
                        {professionalName}
                    </span>
                </div>
            </div>
        </a>
    ) : (
        <Link
            to={link || '#'}
            ref={ref}
            className={css(S.DirectoryGridContainer)}
            prefetch="intent"
            onClick={trackClick}
        >
            <div className={css(S.VideoAspect)}>
                {video}
                <Image
                    className={css(S.Cover)}
                    src={videoCoverDesktop}
                    width={tileWidth}
                    height={tileHeight}
                    layout={'fill'}
                    objectFit={'cover'}
                    objectPosition={'center'}
                    alt={title || ''}
                    styles={{ height: '100%' }}
                />
            </div>
            <div className={css(S.PotmDetails)}>
                <div className="flex flex-col gap-[0.125rem]">
                    <span className="overflow-hidden text-ellipsis line-clamp-2 text-sm md:text-base font-medium tracking-[0.03rem] md:tracking-[0.02rem]">
                        {title}
                    </span>
                    <span className="overflow-hidden text-ellipsis text-nowrap text-sm md:text-base font-medium tracking-[0.03rem] md:tracking-[0.02rem] text-gray-900/40">
                        {professionalName}
                    </span>
                </div>
            </div>
        </Link>
    );
};

export default AdvertPotmTileDesktop;
